import React, { useEffect, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Address } from '@/api/subscriptions/types';
import { DeliveryFeeModal } from '@/components/delivery-fee-modal';
import { isDesktop } from '@/core';
import useSearchStore from '@/stores/use-search-store';
import { Button, Text } from '@/ui';
import { CheckItem } from '@/ui/check-item';
import GoogleAutocomplete from '@/ui/google-autocomplete';
import { Icon } from '@/ui/icon';
import { CaretDown, Search } from '@/ui/icons';
import { CaretUp } from '@/ui/icons/caret-up';

interface DeliveryDropdownProps {
  onOpen?: () => void;
  onClose?: () => void;
  onReset?: () => void;
  opened?: boolean;
  active?: boolean;
  style?: any;
  initialDeliveryAddress?: Address | null;
  initialReturnAddress?: Address | null;
}

export default function DeliveryDropdown({
  onOpen,
  onClose,
  onReset,
  opened = false,
  active = false,
  style,
  initialDeliveryAddress,
  initialReturnAddress,
}: DeliveryDropdownProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [expanded, setExpanded] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);
  const [hover, setHover] = React.useState(false);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [deliveryAddress, setDeliveryAddress] = useState<Address | null>(null);
  const [returnAddress, setReturnAddress] = useState<Address | null>(null);
  const setDeliveryDeliverToAddress = useSearchStore(
    (state) => state.setDeliveryDeliverToAddress
  );
  const setDeliveryReturnToAddress = useSearchStore(
    (state) => state.setDeliveryReturnToAddress
  );
  const setDeliveryModeEnabled = useSearchStore(
    (state) => state.setDeliveryModeEnabled
  );
  const [checkedReturnAddress, setCheckedReturnAddress] = useState(false);
  const [showHowDeliveryFeesModal, setShowHowDeliveryFeesModal] =
    React.useState(false);

  const pressableRef = useRef<View>(null);

  useEffect(() => {
    setExpanded(opened);
  }, [opened]);

  useEffect(() => {
    setDeliveryAddress(initialDeliveryAddress);

    setReturnAddress(initialReturnAddress);
  }, [initialDeliveryAddress, initialReturnAddress]);

  useEffect(() => {
    if (!deliveryAddress) {
      setCheckedReturnAddress(false);
      setReturnAddress(null);
    }
  }, [deliveryAddress]);

  const getButtonStyle = () => {
    let stateStyle = styles.default;

    if (active) {
      stateStyle = styles.active;
    }

    if (hover) {
      stateStyle = styles.hover;
    }

    return [styles.button, stateStyle];
  };

  const handlePressableLayout = () => {
    pressableRef.current?.measure((fx, fy, width, height, px, py) => {
      setModalPosition({ top: py + height, left: px });
    });
  };

  return (
    <View style={style}>
      <Pressable
        ref={pressableRef}
        onLayout={handlePressableLayout}
        onPress={() => {
          if (expanded === false && onOpen) {
            onOpen();
          }

          if (expanded === true && onClose) {
            onClose();
          }

          setExpanded(!expanded);
        }}
        hitSlop={8}
        onPressIn={() => setPressed(true)}
        onPressOut={() => setPressed(false)}
        style={[getButtonStyle(), { opacity: pressed ? 0.5 : 1 }]}
        onHoverIn={() => setHover(true)}
        onHoverOut={() => setHover(false)}
      >
        <Text type="bodyRegular">Deliver To Me</Text>

        <Icon
          type="naked"
          icon={expanded ? <CaretUp /> : <CaretDown />}
          width={10}
          height={10}
        />
      </Pressable>

      <View>
        <Modal
          animationIn="slideInUp"
          animationInTiming={isDesktop() ? 1 : 300}
          animationOut="slideOutDown"
          animationOutTiming={isDesktop() ? 1 : 300}
          hasBackdrop
          backdropOpacity={0.3}
          isVisible={expanded}
          style={{
            margin: 0,
            position: 'absolute',
            top: modalPosition.top + 10,
            left: modalPosition.left,
          }}
          onBackdropPress={() => {
            setExpanded(false);
            onClose?.();
          }}
          onDismiss={() => {}}
        >
          <View
            style={{
              minHeight: 200,
              width: 400,
              marginTop: 'auto',
              backgroundColor: theme.colors.background_2,
              borderRadius: 16,
              flexDirection: 'column',
              gap: 16,
              padding: 24,
            }}
          >
            <View style={{ gap: 16 }}>
              <View style={{ flexGrow: 1, width: '100%', zIndex: 2 }}>
                <GoogleAutocomplete
                  label="Delivery Address"
                  onPress={(place: Address) => {
                    setDeliveryAddress(place);

                    if (checkedReturnAddress) {
                      setReturnAddress(place);
                    }
                  }}
                  inputStyle={{ width: 300 }}
                  address={deliveryAddress}
                  onClear={() => {
                    setDeliveryAddress(null);
                  }}
                  placeholderTextColor={theme.colors.text.secondary}
                  placeholder="Delivery address"
                  leftIcon={
                    <Icon
                      type="naked"
                      icon={<Search />}
                      fillColor={theme.colors.icon.color}
                      width={22}
                      height={22}
                    />
                  }
                />
              </View>

              <View style={{ flexGrow: 1, width: '100%', zIndex: 1 }}>
                <GoogleAutocomplete
                  label="Return Address"
                  onPress={(place: Address) => {
                    setReturnAddress(place);
                  }}
                  inputStyle={{ width: 300 }}
                  address={returnAddress}
                  disabled={checkedReturnAddress}
                  onClear={() => {
                    setReturnAddress(null);
                  }}
                  placeholderTextColor={theme.colors.text.secondary}
                  placeholder="Return address"
                  leftIcon={
                    <Icon
                      type="naked"
                      icon={<Search />}
                      fillColor={theme.colors.icon.color}
                      width={22}
                      height={22}
                    />
                  }
                />
              </View>

              {deliveryAddress ? (
                <View style={{ zIndex: 0 }}>
                  <CheckItem
                    text="Return address is the same address"
                    onPress={() => {
                      if (checkedReturnAddress) {
                        setReturnAddress(null);
                      } else {
                        setReturnAddress(deliveryAddress);
                      }

                      setCheckedReturnAddress(!checkedReturnAddress);
                    }}
                    active={checkedReturnAddress}
                    type="checkbox"
                  />
                </View>
              ) : null}
            </View>

            <Button
              text="How we calculate delivery fees"
              type="link"
              onPress={() => {
                setShowHowDeliveryFeesModal(true);
              }}
              style={{ width: 240, padding: 0, zIndex: -1 }}
            />

            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                borderTopWidth: 1,
                borderTopColor: theme.colors.card.default.borderColor,
                alignItems: 'center',
                zIndex: -1,
              }}
            >
              <View
                style={{
                  paddingTop: 8,
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Button
                  tx="buttonFilter.clear"
                  type="ghost"
                  onPress={() => {
                    setDeliveryAddress(null);
                    setReturnAddress(null);
                    setDeliveryDeliverToAddress(null);
                    setDeliveryReturnToAddress(null);
                    setDeliveryModeEnabled(false);
                    setCheckedReturnAddress(false);
                    onReset?.();
                  }}
                  style={{
                    width: 'auto',
                    paddingLeft: 0,
                  }}
                  disabled={deliveryAddress === null && returnAddress === null}
                />

                <Button
                  tx="search.search"
                  type="primary"
                  onPress={() => {
                    setExpanded(false);

                    setDeliveryDeliverToAddress(deliveryAddress);

                    setDeliveryReturnToAddress(returnAddress);

                    setDeliveryModeEnabled(true);
                  }}
                  style={{ width: 180 }}
                  disabled={deliveryAddress === null || returnAddress === null}
                />
              </View>
            </View>
          </View>
        </Modal>
      </View>

      <DeliveryFeeModal
        visible={showHowDeliveryFeesModal}
        onClose={() => {
          setShowHowDeliveryFeesModal(false);
        }}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  button: {
    paddingTop: 8,
    paddingRight: 16,
    paddingBottom: 8,
    paddingLeft: 16,
    borderRadius: 8,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
  },
  default: {
    backgroundColor: theme.colors.buttonFilter.default.backgroundColor,
    borderColor: theme.colors.buttonFilter.default.border,
  },
  hover: {
    backgroundColor: theme.colors.buttonFilter.hover.backgroundColor,
    borderColor: theme.colors.buttonFilter.hover.border,
  },
  active: {
    backgroundColor: theme.colors.buttonFilter.active.backgroundColor,
    borderColor: theme.colors.buttonFilter.active.border,
  },
}));
